/**
 * Choosing how carefully the agent should work.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS SHOWN, AND WHY IT IS CREDITS RATHER THAN MONEY
 *
 * A turn on Pilot High costs about $0.02. Shown as money that rounds to nothing
 * and tells a customer nothing; shown as "98 credits, ~204 questions from your
 * monthly allowance" it becomes a decision they can actually make. Credits are
 * the unit their budget is denominated in, so credits are the unit the choice
 * is priced in.
 *
 * Accuracy is shown too, and it is the MEASURED worst-case figure from
 * scripts/bench-models.ts rather than a marketing adjective. "94%" invites the
 * right question — 94% of what — and the honest answer is on the tin: our own
 * task set, worst pass. A tier described as "most powerful" invites no question
 * at all, which is the problem with describing it that way.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AUTO IS THE DEFAULT, AND IT IS FIRST
 *
 * Most people should not be picking a model per question. Auto reads the
 * request and spends accordingly — cheap on a lookup, careful on a
 * reconciliation — and reports which tier it chose afterwards, so the choice is
 * visible without being a decision.
 */

import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { api, ApiError } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

export type Tier = {
  id: string;
  name: string;
  name_ar: string;
  blurb: string;
  blurb_ar: string;
  credits_per_exchange: number;
  credits_per_exchange_thinking?: number;
  exchanges_per_20k: number;
  thinks: boolean;
  relative: number;
  accuracy?: number;
  seconds?: number;
};

export const AUTO = 'auto';

export function useTiers() {
  return useQuery<{ models: Tier[]; auto: string; remaining: number }, ApiError>({
    queryKey: ['agent-models'],
    queryFn: () => api('/agent/models'),
    /* The catalogue changes when we ship, not while somebody is typing. */
    staleTime: 10 * 60_000,
  });
}

export function ModelPicker({
  value,
  onChange,
  thinking,
  onThinkingChange,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  thinking: boolean;
  onThinkingChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const { t, lang, dir } = useI18n();
  const ar = lang === 'ar';
  const locale = ar ? 'ar-BH-u-nu-latn' : 'en';
  const { data } = useTiers();

  const tiers = data?.models ?? [];
  const current = tiers.find((m) => m.id === value);
  const label = value === AUTO ? t('model_auto') : ((ar ? current?.name_ar : current?.name) ?? t('model_auto'));

  /* Thinking is only offered where the tier can actually do it — and on Auto,
     where the router decides per turn. Showing a dead toggle is worse than
     hiding it. */
  const canThink = value === AUTO || Boolean(current?.thinks);

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-7 gap-1 px-2 text-[11.5px] font-medium text-muted-foreground hover:text-foreground"
          >
            {/*
              No glyph. A wand next to "Auto" and a sparkle next to "Think" are
              decoration on controls whose whole job is to be read: the words
              already say what they do, and the icons only competed with them.
              The chevron stays because it is not decoration — it is the only
              thing distinguishing a menu from a label.
            */}
            {label}
            <ChevronDown className="size-3" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align={dir === 'rtl' ? 'end' : 'start'} className="w-80">
          <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
            {t('model_pick_hint')}
          </DropdownMenuLabel>

          <DropdownMenuItem onSelect={() => onChange(AUTO)} className="gap-2 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium">{t('model_auto')}</span>
                {value === AUTO ? <Check className="size-3.5" /> : null}
              </div>
              <p className="text-[11.5px] text-muted-foreground">{t('model_auto_hint')}</p>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {tiers.map((m) => (
            <DropdownMenuItem key={m.id} onSelect={() => onChange(m.id)} className="gap-2 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium">{ar ? m.name_ar : m.name}</span>
                  {value === m.id ? <Check className="size-3.5" /> : null}
                  <span className="ms-auto text-[11px] tabular-nums text-muted-foreground">
                    {t('model_credits_each').replace(
                      '{n}',
                      m.credits_per_exchange.toLocaleString(locale),
                    )}
                  </span>
                </div>
                <p className="text-[11.5px] text-muted-foreground">{ar ? m.blurb_ar : m.blurb}</p>
                {/*
                  The three numbers that make the tiers comparable: how many
                  questions the monthly allowance buys, the measured accuracy,
                  and how long a turn takes. Without the first, "98 credits" is
                  not a quantity anybody can feel.
                */}
                <p className="mt-0.5 text-[10.5px] tabular-nums text-muted-foreground">
                  {t('model_per_month').replace('{n}', m.exchanges_per_20k.toLocaleString(locale))}
                  {m.accuracy ? ` · ${(m.accuracy * 100).toFixed(0)}%` : ''}
                  {m.seconds ? ` · ${m.seconds.toLocaleString(locale)}s` : ''}
                </p>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {canThink ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          aria-pressed={thinking}
          onClick={() => onThinkingChange(!thinking)}
          title={
            thinking && current?.credits_per_exchange_thinking
              ? t('model_thinking_cost').replace(
                  '{n}',
                  current.credits_per_exchange_thinking.toLocaleString(locale),
                )
              : t('model_thinking_hint')
          }
          className={cn(
            'h-7 px-2 text-[11.5px] font-medium',
            /* On is a filled chip, off is plain text. With no icon that
               contrast is the only thing carrying the state, so it has to be
               unmistakable rather than a tint. */
            thinking
              ? 'bg-foreground text-background hover:bg-foreground/90 hover:text-background'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t('model_thinking')}
        </Button>
      ) : null}
    </div>
  );
}
